<script setup lang="ts">
import { ref } from 'vue'
import type { Competition, CompetitionSettings, PartConstraint } from '../api/competitions'
import CButton from './CButton.vue'
import CInput from './CInput.vue'
import CTextarea from './CTextarea.vue'
import CPartConstraintEditor from './CPartConstraintEditor.vue'

const props = defineProps<{
	competition: Competition
}>()

const emit = defineEmits<{
	settingsUpdated: [data: CompetitionSettings]
	stageChanged: [stage: 'accepting_submissions' | 'closed']
}>()

const formVisible = ref(false)

const name = ref(props.competition.name)
const description = ref(props.competition.description ?? '')
const password = ref(props.competition.remote_orchestration_password ?? '')
const maxSubmissions = ref(props.competition.max_submissions ?? 1)
const partConstraints = ref<PartConstraint[]>(props.competition.part_constraints ?? [])
const minSizeX = ref<number | null>(props.competition.min_size_x)
const maxSizeX = ref<number | null>(props.competition.max_size_x)
const minSizeY = ref<number | null>(props.competition.min_size_y)
const maxSizeY = ref<number | null>(props.competition.max_size_y)
const minSizeZ = ref<number | null>(props.competition.min_size_z)
const maxSizeZ = ref<number | null>(props.competition.max_size_z)
const requiredHullMaterial = ref(props.competition.required_hull_material ?? '')

function nullableFloat(v: number | null | string): number | null {
	if (v === null || v === '' || v === undefined) return null
	const n = Number(v)
	return isNaN(n) ? null : n
}

function submitSettings() {
	emit('settingsUpdated', {
		name: name.value,
		description: description.value,
		remote_orchestration_password: password.value,
		max_submissions: maxSubmissions.value,
		part_constraints: partConstraints.value.length ? partConstraints.value : null,
		min_size_x: nullableFloat(minSizeX.value),
		max_size_x: nullableFloat(maxSizeX.value),
		min_size_y: nullableFloat(minSizeY.value),
		max_size_y: nullableFloat(maxSizeY.value),
		min_size_z: nullableFloat(minSizeZ.value),
		max_size_z: nullableFloat(maxSizeZ.value),
		required_hull_material: requiredHullMaterial.value.trim() || null,
	})
}
</script>

<template>
	<section class="c-panel l-stack--sm">
		<h2>Organizer Controls</h2>

		<div class="l-stack-horizontal--sm">
			<span>
				<CButton @click="formVisible = !formVisible">
					{{ formVisible ? 'Hide Settings' : 'Edit Settings' }}
				</CButton>
			</span>

			<span>
				<CButton :href="`/api/competitions/${competition.id}/vessels.zip`">
					Download Vessels
				</CButton>
			</span>

			<span v-if="competition.status === 'accepting_submissions'">
				<CButton variant="danger" @click="emit('stageChanged', 'closed')">
					Close Submissions
				</CButton>
			</span>

			<span v-if="competition.status === 'closed'">
				<CButton variant="danger" @click="emit('stageChanged', 'accepting_submissions')">
					Re-open Submissions
				</CButton>
			</span>
		</div>

		<form v-if="formVisible" class="l-stack" style="margin-top: 1rem;" @submit.prevent="submitSettings">
			<CInput v-model="name" type="text" name="name" title="Competition Name" :wide="true" />
			<CInput v-model="password" type="text" name="remote_orchestration_password" title="Remote Orchestration Password" :wide="true" />
			<CInput v-model.number="maxSubmissions" type="number" name="max_submissions" title="Max Vessels Per Player" :wide="true" min="1" max="99" />
			<CTextarea v-model="description" name="description" label="Description (accepts Markdown)" />

			<fieldset class="c-organizer-constraints">
				<legend>Craft Constraints</legend>

				<div class="l-stack--sm">
					<div>
						<label class="c-organizer-constraints__label">Part Constraints <small>(submissions are blocked if violated)</small></label>
						<CPartConstraintEditor v-model="partConstraints" />
					</div>

					<div>
						<label class="c-organizer-constraints__label">Size Limits <small>(warns submitter only; based on craft size field)</small></label>
						<table class="c-organizer-constraints__size-table">
							<thead>
								<tr>
									<th></th>
									<th>Min (m)</th>
									<th>Max (m)</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>X (width)</td>
									<td><input v-model.number="minSizeX" type="number" step="0.1" min="0" placeholder="—" class="c-organizer-constraints__size-input" /></td>
									<td><input v-model.number="maxSizeX" type="number" step="0.1" min="0" placeholder="—" class="c-organizer-constraints__size-input" /></td>
								</tr>
								<tr>
									<td>Y (height)</td>
									<td><input v-model.number="minSizeY" type="number" step="0.1" min="0" placeholder="—" class="c-organizer-constraints__size-input" /></td>
									<td><input v-model.number="maxSizeY" type="number" step="0.1" min="0" placeholder="—" class="c-organizer-constraints__size-input" /></td>
								</tr>
								<tr>
									<td>Z (length)</td>
									<td><input v-model.number="minSizeZ" type="number" step="0.1" min="0" placeholder="—" class="c-organizer-constraints__size-input" /></td>
									<td><input v-model.number="maxSizeZ" type="number" step="0.1" min="0" placeholder="—" class="c-organizer-constraints__size-input" /></td>
								</tr>
							</tbody>
						</table>
					</div>

					<div>
						<CInput
							v-model="requiredHullMaterial"
							type="text"
							name="required_hull_material"
							title="Required Hull Material"
							:wide="true"
							placeholder="e.g. Aluminium (leave blank to disable)"
						/>
						<small>Warns only. Case-sensitive. Parts without a HitpointTracker module are skipped.</small>
					</div>
				</div>
			</fieldset>

			<CButton type="submit">Update Settings</CButton>
		</form>
	</section>
</template>

<style scoped>
.c-organizer-constraints {
	border: 2px solid var(--color-form-field-border);
	padding: 0.75rem 1rem;
}

.c-organizer-constraints legend {
	padding: 0 0.4rem;
	font-weight: bold;
}

.c-organizer-constraints__label {
	display: block;
	margin-bottom: 0.4rem;
	font-weight: 500;
}

.c-organizer-constraints__label small {
	font-weight: normal;
	opacity: 0.7;
}

.c-organizer-constraints__size-table {
	border-collapse: collapse;
}

.c-organizer-constraints__size-table th,
.c-organizer-constraints__size-table td {
	padding: 0.25rem 0.5rem;
	text-align: left;
}

.c-organizer-constraints__size-input {
	all: inherit;
	width: 6rem;
	padding: 0.5rem;
	margin: unset;
	line-height: 1;
	background: var(--color-form-field-bg);
	border: 2px solid var(--color-form-field-border);
	box-sizing: border-box;
}

.c-organizer-constraints__size-input:focus {
	border-color: var(--color-primary);
	outline: none;
}
</style>
